import { useState } from 'react'
import type { JSX } from 'react'
import type { FormEvent } from 'react'

import { useMutation } from '@apollo/client/react'

import { Button, Dialog, Form, Input, Text, toast } from '@buffer-mono/popcorn'

import { graphql } from '~publish/graphql'

import style from './DisableTFADialog.module.css'

export const TWO_FACTOR_DISABLE = graphql(/* GraphQL */ `
  mutation DisableTwoFactorWithPassword(
    $input: DisableTwoFactorWithPasswordInput!
  ) {
    disableTwoFactorWithPassword(input: $input) {
      ... on EmptySuccess {
        __typename
      }
      ... on InvalidInputError {
        message
      }
    }
  }
`)

interface DisableTFADialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess?: () => void
}

export function DisableTFADialog({
  open,
  onOpenChange,
  onSuccess,
}: DisableTFADialogProps): JSX.Element {
  const [password, setPassword] = useState('')

  const [disableTwoFactor, mutationResult] = useMutation(TWO_FACTOR_DISABLE)
  const { data, error, loading } = mutationResult

  const errorMessage = error
    ? 'Something went wrong. Please try again.'
    : data && data.disableTwoFactorWithPassword.__typename !== 'EmptySuccess'
      ? data.disableTwoFactorWithPassword.message
      : null

  function handleClose(): void {
    if (loading) return
    setPassword('')
    mutationResult.reset()
    onOpenChange(false)
  }

  function handleSubmit(e: FormEvent): void {
    e.preventDefault()
    disableTwoFactor({
      variables: {
        input: {
          password,
        },
      },
      onError: () => {
        setPassword('')
      },
      onCompleted: (completedData) => {
        if (
          completedData.disableTwoFactorWithPassword.__typename ===
          'EmptySuccess'
        ) {
          toast.success('Two factor authentication has been disabled')
          setPassword('')
          onOpenChange(false)
          onSuccess?.()
        } else {
          setPassword('')
        }
      },
    })
  }

  return (
    <Dialog
      modal
      open={open}
      onOpenChange={(isOpen): void => {
        if (!isOpen) {
          handleClose()
        }
      }}
    >
      <Dialog.Content>
        <Form onSubmit={handleSubmit} className={style.form}>
          <Dialog.Header>
            <Dialog.Title>Disable Two Factor Authentication</Dialog.Title>
            <Dialog.Description>
              Enter your account password to disable two factor authentication.
            </Dialog.Description>
          </Dialog.Header>
          <Dialog.Body>
            <Form.Field name="password">
              <Form.Label>Password</Form.Label>
              <Form.Control>
                <Input
                  type="password"
                  autoComplete="current-password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e): void => setPassword(e.target.value)}
                  aria-invalid={errorMessage ? true : undefined}
                  aria-describedby={errorMessage ? 'password-error' : undefined}
                  disabled={loading}
                  required
                />
              </Form.Control>
              {errorMessage && (
                <Text
                  color="critical"
                  size="sm"
                  id="password-error"
                  role="alert"
                >
                  {errorMessage}
                </Text>
              )}
            </Form.Field>
          </Dialog.Body>
          <Dialog.Footer>
            <Button
              variant="tertiary"
              type="button"
              onClick={handleClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              type="submit"
              disabled={!password.trim() || loading}
            >
              {loading ? 'Disabling...' : 'Disable'}
            </Button>
          </Dialog.Footer>
        </Form>
        <Dialog.CloseButton />
      </Dialog.Content>
    </Dialog>
  )
}
